import { FC, ReactNode } from "react";
import styled, { css, CSSObject } from "styled-components";

import { usePopperParameters, useTooltip } from "./tooltip.hooks";
import { TooltipPlacement } from "./tooltip.types";
import { placementDependentStyles } from "./placementDependentStyles";

import { alpha } from "../../typography";

export enum TooltipTestIds {
  Container = "tooltip",
  Tooltip = "tooltip_tooltip",
}

type StyledTooltipProps = {
  show: boolean;
  placement: TooltipPlacement;
  customTooltipStyles?: CSSObject;
};

const StyledTooltip = styled.div<StyledTooltipProps>`
  --accent-color: var(--color-secondary-600);

  position: relative;
  z-index: 1000;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity ease-in-out 150ms;
  ${placementDependentStyles({
    top: css`
      padding-bottom: 8px;
    `,
    right: css`
      padding-left: 8px;
    `,
    bottom: css`
      padding-top: 8px;
    `,
    left: css`
      padding-right: 8px;
    `,
  })}

  pointer-events: ${(props) => (props.show ? "auto" : "none")};

  ${(props) =>
    css`
      ${props.customTooltipStyles}
    `};
`;

const ReferenceElement = styled.span`
  display: inline-block;
`;

type TooltipContentProps = {
  placement: TooltipPlacement;
  customContentStyles?: CSSObject;
};

const TooltipContent = styled.div<TooltipContentProps>`
  position: relative;
  background-color: var(--color-neutral-000-full);
  border-radius: var(--size-border-radius-small);
  box-shadow: var(--elevation-4);
  overflow: hidden;
  padding: 12px;
  ${alpha.primaryActive.body3};
  ${(props) =>
    css`
      ${props.customContentStyles}
    `};
  ::after {
    content: "";
    position: absolute;
    ${placementDependentStyles({
      top: css`
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
      `,
      right: css`
        left: 0;
        top: 0;
        bottom: 0;
        width: 2px;
      `,
      bottom: css`
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
      `,
      left: css`
        right: 0;
        top: 0;
        bottom: 0;
        width: 2px;
      `,
    })};

    background-color: var(--accent-color);
  }
`;

type StyledArrowProps = {
  placement: TooltipPlacement;
};

const StyledArrow = styled.div<StyledArrowProps>`
  position: absolute;
  mix-blend-mode: lighten;

  ::before {
    content: "";
    margin: auto;
    display: block;
    width: 8px;
    height: 8px;
    transform: rotate(45deg);
    background-color: var(--accent-color);
  }

  ${placementDependentStyles({
    top: css`
      bottom: 4px;
    `,
    right: css`
      left: 4px;
    `,
    bottom: css`
      top: 4px;
    `,
    left: css`
      right: 4px;
    `,
  })};
`;

type TooltipProps = {
  /** Unique identifier for the component. */
  id?: string;
  /** Unique identifier for testing the component that overrides the default test id. */
  testId?: string;
  /** ClassName property to style the component. */
  className?: string;
  /** The content that will be rendered inside of the Tooltip. */
  content: ReactNode;
  /** Where to place the Tooltip relative to the reference element that triggers it. If there is not enough space in the viewport, the Tooltip will move to the opposite side. */
  defaultPlacement?: TooltipPlacement;
  /** Custom tooltip styles */
  customTooltipStyles?: CSSObject;
  /** Custom content styles */
  customContentStyles?: CSSObject;
  popperOptions?: usePopperParameters[2];
};

export const Tooltip: FC<TooltipProps> = ({
  id,
  testId,
  className,
  content,
  defaultPlacement = "top",
  customTooltipStyles,
  customContentStyles,
  popperOptions,
  children,
}) => {
  const {
    arrowStyles,
    tooltipAttributes,
    tooltipStyles,
    placement,
    setArrowElement,
    setReferenceElement,
    setTooltipElement,
    shouldShowTooltip,
    showTooltip,
    hideTooltip,
  } = useTooltip({ defaultPlacement, popperOptions });

  return (
    <ReferenceElement
      id={id}
      className={className}
      data-testid={testId ?? TooltipTestIds.Container}
      ref={setReferenceElement}
      onMouseOver={showTooltip}
      onMouseOut={hideTooltip}
    >
      {children}
      <StyledTooltip
        data-testid={testId ? `${testId}_tooltip}` : TooltipTestIds.Tooltip}
        ref={setTooltipElement}
        style={tooltipStyles}
        placement={placement}
        {...tooltipAttributes}
        show={shouldShowTooltip}
        customTooltipStyles={customTooltipStyles}
      >
        <TooltipContent
          placement={placement}
          customContentStyles={customContentStyles}
        >
          {content}
        </TooltipContent>
        <StyledArrow
          ref={setArrowElement}
          style={arrowStyles}
          placement={placement}
        />
      </StyledTooltip>
    </ReferenceElement>
  );
};
